import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, InputAdornment, List, ListItem, ListItemText, CircularProgress } from '@mui/material';
import { Search as SearchIcon, Place as PlaceIcon } from '@mui/icons-material';
import { GlassPanel } from 'liquidglass-react';

interface LocationSearchBarProps {
  onLocationSelect: (lon: number, lat: number, name: string) => void;
}

interface Prediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface AutocompleteResponse {
  status: string;
  predictions: Prediction[];
}

interface PlaceDetailsResponse {
  status: string;
  result: {
    name?: string;
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
    };
  };
}

export default function LocationSearchBar({ onLocationSelect }: LocationSearchBarProps) {
  const [inputValue, setInputValue] = useState('');
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortController = useRef<AbortController | null>(null);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (!value.trim()) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    debounceTimer.current = setTimeout(() => {
      fetchPredictions(value);
    }, 300);
  };

  const fetchPredictions = async (input: string) => {
    // Cancel any pending request
    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();

    try {
      // Use proxied endpoint - backend retrieves API key from Secrets Manager
      const response = await fetch(
        `/api/places/autocomplete/json?input=${encodeURIComponent(input)}`,
        { signal: abortController.current.signal }
      );

      const data: AutocompleteResponse = await response.json();

      setIsLoading(false);
      if (data.status === 'OK' && data.predictions) {
        setPredictions(data.predictions);
        setShowDropdown(true);
      } else {
        setPredictions([]);
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Failed to fetch predictions:', error);
        setIsLoading(false);
        setPredictions([]);
      }
    }
  };

  const handlePredictionSelect = async (prediction: Prediction) => {
    setIsLoading(true);

    try {
      // Use proxied endpoint - backend retrieves API key from Secrets Manager
      const response = await fetch(
        `/api/places/details/json?place_id=${encodeURIComponent(prediction.place_id)}&fields=geometry,name`
      );

      const data: PlaceDetailsResponse = await response.json();

      setIsLoading(false);
      if (data.status === 'OK' && data.result?.geometry?.location) {
        const lat = data.result.geometry.location.lat;
        const lon = data.result.geometry.location.lng;
        const name = data.result.name || prediction.description;

        setInputValue(prediction.description);
        setPredictions([]);
        setShowDropdown(false);

        // Call the callback with coordinates
        onLocationSelect(lon, lat, name);
      }
    } catch (error) {
      console.error('Failed to fetch place details:', error);
      setIsLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      inputRef.current?.blur();
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: 350,
        maxWidth: '90vw',
      }}
    >
      <GlassPanel cornerRadius={9999}>
        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => predictions.length > 0 && setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search location..."
          autoComplete="off"
          size="small"
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'transparent',
              borderRadius: '9999px',
              color: 'white',
              '& fieldset': {
                border: 'none',
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              },
            },
            '& .MuiInputBase-input': {
              color: 'white',
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              </InputAdornment>
            ),
            endAdornment: isLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              </InputAdornment>
            ) : null,
          }}
        />
      </GlassPanel>

      {showDropdown && predictions.length > 0 && (
        <GlassPanel
          cornerRadius={16}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 8,
            maxHeight: 300,
            overflow: 'auto',
            zIndex: 100,
          }}
        >
          <List dense>
            {predictions.map((prediction) => (
              <ListItem
                key={prediction.place_id}
                onClick={() => handlePredictionSelect(prediction)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <PlaceIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', mr: 1.5, fontSize: 20 }} />
                <ListItemText
                  primary={prediction.structured_formatting.main_text}
                  secondary={prediction.structured_formatting.secondary_text}
                  primaryTypographyProps={{
                    sx: { color: 'white', fontSize: '0.9rem' },
                  }}
                  secondaryTypographyProps={{
                    sx: { color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem' },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </GlassPanel>
      )}
    </Box>
  );
}
